import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useInView,
  AnimatePresence,
} from "framer-motion";
import MiniTitle from "layouts/MiniTitle";
import MainTitle from "layouts/MainTitle";

const FALLBACK_ICONS = [
  "https://via.placeholder.com/80/FF6B6B/FFFFFF?text=A",
  "https://via.placeholder.com/80/4ECDC4/FFFFFF?text=B",
  "https://via.placeholder.com/80/45B7D1/FFFFFF?text=C",
  "https://via.placeholder.com/80/96CEB4/FFFFFF?text=D",
];

const API_URL =
  "https://algoritm-api-edu.uz/api/public/MainPageUi/GetAllAboutPages";

/* ====================== MODAL ====================== */
const AboutModal = React.memo(({ item, onClose }) => {
  if (!item) return null;

  const imageUrl = item.imageLink
    ? `https://algoritm-api-edu.uz/api/Public/Content/DownloadFile?fileId=${
        item.imageLink.includes("fileId=")
          ? item.imageLink.split("fileId=")[1]
          : item.imageLink
      }`
    : null;

  return (
    <AnimatePresence>
      <motion.div
        className="about-modal-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="about-modal-content premium"
          initial={{ scale: 0.9, y: 60, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.9, y: 60, opacity: 0 }}
          transition={{ type: "spring", damping: 30, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Yopish"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          <div className="modal-inner">
            {imageUrl ? (
              <div className="modal-hero-image">
                <img src={imageUrl} alt={item.title} className="hero-img" />
                <div className="hero-overlay"></div>
              </div>
            ) : (
              <div className="profile-avatar-corner">
                <div className="avatar-circle">
                  <span>{item.title?.[0] || "S"}</span>
                </div>
              </div>
            )}

            <div className="modal-text-content">
              <h2 className="modal-main-title">{item.title}</h2>
              <p className="modal-full-description">
                {item.fullDescription || "To‘liq ma’lumot mavjud emas."}
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
});
AboutModal.displayName = "AboutModal";

/* ====================== CARD ====================== */
const ParallaxCard = React.memo(({ item, index, onClick, colorClass }) => {
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: true, margin: "-100px" });

  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ["start end", "end start"],
  });

  const y = useTransform(
    scrollYProgress,
    [0, 1],
    index % 2 === 0 ? [80, -80] : [60, -100]
  );
  const rotate = useTransform(
    scrollYProgress,
    [0, 1],
    index % 2 === 0 ? [6, -4] : [-6, 4]
  );

  const imageUrl = item.imageLink
    ? `https://algoritm-api-edu.uz/api/Public/Content/DownloadFile?fileId=${
        item.imageLink.includes("fileId=")
          ? item.imageLink.split("fileId=")[1]
          : item.imageLink
      }`
    : null;

  return (
    <motion.div
      ref={cardRef}
      style={{ y, rotate }}
      initial={{ opacity: 0, y: 100 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.15 }}
      onClick={() => onClick(item)}
      className={`about-card ${colorClass}`}
      // whileHover={{ y: -12 }}
      // whileTap={{ scale: 0.98 }}
    >
      <div className="about-card_icon">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={item.title}
            className="card-main-image"
            loading="lazy"
            onError={(e) => {
              e.target.style.display = "none";
              e.target.parentElement.classList.add("no-image");
            }}
          />
        ) : (
          <div className="fallback-avatar">
            <img src={FALLBACK_ICONS[index % 4]} alt="fallback" />
          </div>
        )}
        <div className="image-overlay"></div>
      </div>

      <h4 className="about-card_title">
        {item.title || "Sarlavha mavjud emas"}
      </h4>
      <p className="about-card_text">
        {item.shortDescription || "Qisqacha tavsif mavjud emas"}
      </p>
    </motion.div>
  );
});
ParallaxCard.displayName = "ParallaxCard";

/* ====================== MAIN COMPONENT ====================== */
const About = () => {
  const [aboutData, setAboutData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const sectionRef = useRef(null);

  const colorClasses = ["", "orange", "green", "last"];

  const fetchAboutData = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);

      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          search: "",
          sortBy: "",
          orderType: "",
          pageSize: 0,
          page: 0,
          startDate: null,
          endDate: null,
        }),
      });

      if (!response.ok) throw new Error("Server xatosi");

      const data = await response.json();
      const items = Array.isArray(data?.rows) ? data.rows : [];
      setAboutData(items);
    } catch (err) {
      console.error("API xatosi:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAboutData();
  }, [fetchAboutData]);

  const handleCardClick = useCallback((item) => {
    setSelectedItem(item);
    document.body.style.overflow = "hidden";
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedItem(null);
    document.body.style.overflow = "unset";
  }, []);

  return (
    <>
      <section ref={sectionRef} id="about" className="about-section">
        <div className="container">
          <MiniTitle>O'quv markaz haqida</MiniTitle>
          <MainTitle>O‘quv markaz haqida ma’lumot</MainTitle>

          <div className="about_cards">
            {loading ? (
              [0, 1, 2, 3].map((i) => (
                <div
                  key={`sk-${i}`}
                  className={`about-card ${colorClasses[i]} skeleton-card`}
                >
                  <div className="skeleton-icon"></div>
                  <div className="skeleton-title"></div>
                  <div className="skeleton-text line-1"></div>
                  <div className="skeleton-text line-2"></div>
                </div>
              ))
            ) : error ? (
              <div className="state-message error">
                <p>Ma'lumot yuklashda xatolik yuz berdi.</p>
                <button onClick={fetchAboutData} className="retry-btn">
                  Qayta yuklash
                </button>
              </div>
            ) : aboutData.length === 0 ? (
              <div className="state-message empty">
                <p>Hozircha ma'lumot mavjud emas.</p>
              </div>
            ) : (
              aboutData.map((item, index) => (
                <ParallaxCard
                  key={item.id || index}
                  item={item}
                  index={index}
                  onClick={handleCardClick}
                  colorClass={colorClasses[index % 4]}
                />
              ))
            )}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {selectedItem && (
          <AboutModal item={selectedItem} onClose={handleCloseModal} />
        )}
      </AnimatePresence>
    </>
  );
};

About.displayName = "AboutSection";
export default About;
